import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { entrepotActions } from "../../sagas/entrepotSlice";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Spinner,
  Table,
  Badge,
  InputGroup,
  InputGroupText,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Label,
  FormGroup,
} from "reactstrap";
import {
  FaCubes,
  FaEdit,
  FaFileExcel,
  FaFileUpload,
  FaSearch,
  FaTimes,
  FaWarehouse,
  FaExchangeAlt,
} from "react-icons/fa";
import Select from "react-select";
import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { stockActions } from "../../sagas/stockSlice";
import * as XLSX from "xlsx";
import Swal from "sweetalert2";

const EntrepotCard = ({ entrepot, active, onClick }) => {
  return (
    <Card
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onClick()}
      className={`mb-3 shadow-sm ${active ? "border border-2 border-primary" : "border-0"}`}
      style={{ cursor: "pointer", transition: "transform .15s ease-in-out" }}
      onMouseEnter={(e) => (e.currentTarget.style.transform = "translateY(-2px)")}
      onMouseLeave={(e) => (e.currentTarget.style.transform = "translateY(0)")}
    >
      <CardBody className="d-flex align-items-center">
        <div
          className="me-3 d-flex align-items-center justify-content-center rounded-circle"
          style={{ width: 44, height: 44, background: "rgba(13,110,253,.1)" }}
        >
          <FaWarehouse className="text-primary" />
        </div>
        <div className="flex-grow-1">
          <div className="fw-semibold">{entrepot.label ?? `${entrepot.libelle}`}</div>
        </div>
        {active && <Badge color="primary">Sélectionné</Badge>}
      </CardBody>
    </Card>
  );
};

const StockPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const formatDate = (date = new Date()) => {
    const pad = (n) => String(n).padStart(2, "0");

    return (
      date.getFullYear() +
      "-" +
      pad(date.getMonth() + 1) +
      "-" +
      pad(date.getDate())
    );
  };

  const toDateTime = (dateValue) => {
    if (!dateValue) return "";
    if (dateValue.includes("T")) return dateValue;

    const now = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    return `${dateValue}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
  };

  const { stocks, loading: stockLoading } = useSelector((state) => state.stock);
  const { entrepots, loading: entrepotLoading } = useSelector((state) => state.entrepots);

  const fileInputRef = useRef(null);

  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedEntrepot, setSelectedEntrepot] = useState(null);

  // ---------------- TRANSFER STATE ----------------
  const [transferModal, setTransferModal] = useState(false);
  const [transfer, setTransfer] = useState({
    articleId: null,
    articleNom: "",
    entrepotSrcId: null,
    entrepotSrcNom: "",
    entrepotDestId: null,
    quantite: "",
    maxQuantite: 0,
    dateMouvement: formatDate(),
  });

  const toggleTransferModal = () => setTransferModal((v) => !v);

  // Fetch entrepots
  useEffect(() => {
    dispatch(entrepotActions.list());
  }, []);

  // Fetch stocks whenever selection changes
  useEffect(() => {
    if (selectedEntrepot) {
      dispatch(stockActions.listStocks({ entrepotId: selectedEntrepot?.value }));
    }
  }, [selectedEntrepot]);

  useEffect(() => {
    const id = setTimeout(() => setSearchTerm(searchInput.trim()), 200);
    return () => clearTimeout(id);
  }, [searchInput]);

  const entrepotOptions = useMemo(
    () =>
      (entrepots ?? [])
        .filter((e) => e.libelle !== "EXTERNE" && e.libelle !== "RETOUR")
        .map((entrepot) => ({
          value: entrepot.id,
          label: `${entrepot.code} - ${entrepot.libelle}`,
          raw: entrepot,
        })),
    [entrepots]
  );

  const allOption = { value: null, label: t("common.allWarehouses") || "Tous les entrepôts" };

  // simple case-insensitive contains
  const matches = useCallback((value, q) => {
    if (!q) return true;
    if (!value) return false;
    return String(value).toLowerCase().includes(q.toLowerCase());
  }, []);

  const refrechStocks = () => {
    dispatch(
      stockActions.listStocks({
        entrepotId: selectedEntrepot ? selectedEntrepot.value : null,
      })
    );
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const allowedTypes = [
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel",
      ];
      if (!allowedTypes.includes(file.type)) {
        Swal.fire({
          icon: "error",
          title: t("product.invalidFileType"),
          text: t("product.excelOnly"),
        });
        return;
      }

      try {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("entrepotId", selectedEntrepot?.value);

        let payload = {
          formData: formData,
          onSuccess: () => {
            refrechStocks();
            Swal.fire({
              icon: "success",
              title: t("stock.uploadSuccess"),
              confirmButtonText: "OK",
              confirmButtonColor: "#198754",
            });
          },
        };
        dispatch(stockActions.uploadStocks(payload));
      } catch (error) {
        console.error("Error parsing Excel:", error);
        Swal.fire({
          icon: "error",
          title: "Erreur!",
          text: "Erreur lors de l'importation des produits",
          confirmButtonText: "OK",
          confirmButtonColor: "#dc3545",
        });
      } finally {
        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    }
  };

  const filteredStocks = useMemo(() => {
    if (!stocks) return [];
    if (!searchTerm) return stocks;
    return stocks.filter((s) => {
      return matches(s.articleNom, searchTerm) || matches(s.entrepotNom, searchTerm) || matches(s.articleCodeBarre, searchTerm);
    });
  }, [stocks, searchTerm, matches]);

  const handleExportExcel = useCallback(() => {
    const now = new Date();
    const datePart = now.toISOString().slice(0, 19).replace(/[:T]/g, "-");

    // En-têtes localisés
    const HEAD_ENTREPOT = t("entrepot.libelle") || "Entrepôt";
    const HEAD_ARTICLE = t("product.description") || "Article";
    const HEAD_ARTICLE_ID = "Article ID";
    const HEAD_QTE = t("product.stockDisponible") || "Stock disponible";

    const data = filteredStocks.map((s) => ({
      [HEAD_ENTREPOT]: s.entrepotNom ?? "",
      [HEAD_ARTICLE_ID]: s.articleId ?? "",
      [HEAD_ARTICLE]: s.articleNom ?? "",
      [HEAD_QTE]: s.quantiteDisponible ?? 0,
    }));

    const ws = XLSX.utils.json_to_sheet(data, { skipHeader: false });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Stocks");

    const headers = [HEAD_ENTREPOT, HEAD_ARTICLE_ID, HEAD_ARTICLE, HEAD_QTE];
    const colWidths = headers.map((h) => ({ wch: Math.max(h.length, 12) }));
    data.forEach((row) => {
      headers.forEach((h, idx) => {
        const cellLen = String(row[h] ?? "").length;
        if (cellLen + 2 > (colWidths[idx].wch || 10)) colWidths[idx].wch = cellLen + 2;
      });
    });
    ws["!cols"] = colWidths;

    const entrepotLabel = selectedEntrepot?.label || allOption.label || "tous-entrepots";
    const safeLabel = String(entrepotLabel).toLowerCase().replace(/\s+/g, "-").replace(/[^\w-]/g, "");
    const filename = `stocks_${safeLabel}_${datePart}.xlsx`;

    XLSX.writeFile(wb, filename);
  }, [filteredStocks, selectedEntrepot, t]);

  const handleClickCard = useCallback(
    (id) => {
      if (id === null) {
        setSelectedEntrepot(null);
      } else {
        const found = entrepotOptions.find((o) => o.value === id);
        setSelectedEntrepot(found ? { value: found.value, label: found.label } : null);
      }
    },
    [entrepotOptions]
  );

  const loading = stockLoading || entrepotLoading;

  const handleUpdateQte = (articleId, entrepotId, qteEnStock) => {
    Swal.fire({
      title: t("stock.confirmation"),
      text: t("stock.saveOrConfirmText"),
      icon: "question",
      showCancelButton: true,
      confirmButtonText: t("common.save"),
      cancelButtonText: t("common.cancel"),
    }).then((result) => {
      if (result.isConfirmed) {
        let payload = {
          data: { articleId, entrepotId, quantiteEnStock: qteEnStock },
          onSuccess: () => {
            Swal.fire({
              icon: "success",
              title: t("common.updated"),
              text: t("stock.updatedSuccess"),
            });

            refrechStocks();
          },
        };
        dispatch(stockActions.updateStock(payload));
      }
    });
  };

  // -------- TRANSFER FEATURE ----------
  const openTransferModal = (stock) => {
    // prefill source from row
    setTransfer({
      articleId: stock.articleId,
      articleNom: stock.articleNom,
      entrepotSrcId: stock.entrepotId ?? selectedEntrepot?.value,
      entrepotSrcNom: stock.entrepotNom,
      entrepotDestId: null,
      quantite: "",
      maxQuantite: Number(stock.quantiteDisponible ?? 0),
      dateMouvement: formatDate(),
    });
    setTransferModal(true);
  };

  const handleAddTransfer = (articleId, entrepotSrcId, entrepotDestId, quantite) => {
    Swal.fire({
      title: t("stock.confirmationTransfer") || "Confirmation de transfert",
      text: t("stock.saveOrConfirmTransferText") || "Voulez-vous confirmer ce transfert ?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: t("common.save") || "Valider",
      cancelButtonText: t("common.cancel") || "Annuler",
    }).then((result) => {
      if (result.isConfirmed) {
        // ✅ compat: send both keys (quantite + quantiteTransfered)
        let payload = {
          data: {
            articleId,
            entrepotSrcId,
            entrepotDestId,
            quantite,
            quantiteTransfered: quantite,
            dateMouvement: toDateTime(transfer.dateMouvement),
          },
          onSuccess: () => {
            Swal.fire({
              icon: "success",
              title: t("common.updated") || "Mis à jour",
              text: t("stock.transferSuccess") || "Transfert effectué avec succès",
            });

            refrechStocks();
            setTransferModal(false);
          },
        };
        dispatch(stockActions.transferStock(payload));
      }
    });
  };

  const submitTransfer = () => {
    const qte = Number(transfer.quantite);

    if (!transfer.entrepotSrcId) {
      Swal.fire({ icon: "warning", title: "Entrepôt source", text: "Veuillez sélectionner un entrepôt source." });
      return;
    }

    if (!transfer.entrepotDestId) {
      Swal.fire({ icon: "warning", title: "Entrepôt destination", text: "Veuillez sélectionner un entrepôt destination." });
      return;
    }

    if (transfer.entrepotSrcId === transfer.entrepotDestId) {
      Swal.fire({
        icon: "warning",
        title: "Transfert invalide",
        text: "Entrepôt source et destination doivent être différents.",
      });
      return;
    }

    if (!transfer.quantite || isNaN(qte) || qte <= 0) {
      Swal.fire({ icon: "warning", title: "Quantité", text: "Veuillez saisir une quantité valide > 0." });
      return;
    }

    if (!transfer.dateMouvement) {
      Swal.fire({ icon: "warning", title: "Date du mouvement", text: "Veuillez saisir la date du mouvement." });
      return;
    }

    if (qte > Number(transfer.maxQuantite || 0)) {
      Swal.fire({
        icon: "warning",
        title: "Quantité invalide",
        text: `La quantité ne doit pas dépasser le stock disponible (${transfer.maxQuantite}).`,
      });
      return;
    }

    handleAddTransfer(transfer.articleId, transfer.entrepotSrcId, transfer.entrepotDestId, qte);
  };

  return (
    <>
      {loading ? (
        <div className="text-center py-5">
          <Spinner color="primary" />
          <div className="mt-2 text-muted">{t("common.loading")}</div>
        </div>
      ) : (
        <Container fluid>
          {/* Header */}
          <Row className="mb-4">
            <Col>
              <Card className="shadow-sm">
                <CardBody className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                  <h4 className="mb-0 d-flex align-items-center">
                    <FaCubes className="text-primary me-2" />
                    {t("stock.title")}
                  </h4>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Entrepôt Cards */}
          <Row className="mb-3">
            {(entrepots.filter((e) => e.libelle !== "EXTERNE" && e.libelle !== "RETOUR") ?? []).map((e) => (
              <Col key={e.id} md={3} sm={6} xs={12}>
                <EntrepotCard entrepot={e} active={selectedEntrepot?.value === e.id} onClick={() => handleClickCard(e.id)} />
              </Col>
            ))}
          </Row>

          {/* Search + Export toolbar */}
          <Row className="mb-4">
            <Col>
              <div className="d-flex gap-2 align-items-stretch">
                <div className="flex-grow-1">
                  <InputGroup className="shadow-sm">
                    <InputGroupText>
                      <FaSearch />
                    </InputGroupText>
                    <Input
                      type="text"
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      placeholder={t("common.search") || "Rechercher un article ou un entrepôt..."}
                    />
                    {searchInput && (
                      <Button color="light" onClick={() => setSearchInput("")}>
                        <FaTimes />
                      </Button>
                    )}
                  </InputGroup>
                </div>

                <div className="d-flex mx-3">
                  <Button
                    size="sm"
                    color="success"
                    className="shadow-sm d-flex align-items-center h-100"
                    onClick={handleExportExcel}
                    disabled={!filteredStocks || filteredStocks.length === 0}
                    title={t("common.exportExcel") || "Exporter en Excel"}
                  >
                    <FaFileExcel className="me-2" />
                    {t("common.exportExcel") || "Exporter en Excel"}
                  </Button>

                  <Button color="primary" size="sm" className="mx-2" onClick={() => fileInputRef.current.click()} disabled={!selectedEntrepot}>
                    <FaFileUpload className="me-2" size={12} />
                    {t("stock.upload")}
                  </Button>

                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                    accept=".xlsx,.xls"
                    style={{ display: "none" }}
                    disabled={!selectedEntrepot}
                  />
                </div>
              </div>
            </Col>
          </Row>

          {/* Stocks Table */}
          <Row>
            <div className="table-responsive" style={{ maxHeight: "500px", overflowY: "auto" }}>
              <Table bordered hover>
                <thead>
                  <tr>
                    <th>{t("entrepot.libelle")}</th>
                    <th style={{ width: "250px" }}>{t("product.description")}</th>
                    <th>{t("product.barcode")}</th>
                    <th>{t("product.stockDisponible")}</th>
                    <th>{t("product.stockMinimal")}</th>
                    <th style={{ width: "140px" }}>{t("common.actions") || "Actions"}</th>
                  </tr>
                </thead>

                <tbody>
                  {!selectedEntrepot || !filteredStocks || filteredStocks.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="text-center py-4">
                        <div className="text-muted">{t("stock.noStocks")}</div>
                      </td>
                    </tr>
                  ) : (
                    filteredStocks.map((stock) => (
                      <tr key={stock.id}>
                        <td>{stock.entrepotNom}</td>
                        <td>{stock.articleNom}</td>
                        <td>{stock.articleCodeBarre}</td>

                        {/* Stock cell + edit */}
                        <td className="d-flex align-items-center justify-content-between gap-2">
                          <span>{stock.quantiteDisponible}</span>

                          <Button
                            size="sm"
                            color="light"
                            className="border shadow-sm"
                            title={t("common.edit") || "Modifier"}
                            onClick={() => {
                              Swal.fire({
                                title: t("stock.editStockTitle") || "Modifier le stock disponible",
                                html: `
                                  <div style="text-align:left">
                                    <div style="margin-bottom:6px;font-weight:600">${stock.articleNom}</div>
                                    <div style="font-size:12px;color:#666">${stock.entrepotNom}</div>
                                  </div>
                                `,
                                input: "number",
                                inputLabel: t("product.stockDisponible") || "Stock disponible",
                                inputValue: stock.quantiteDisponible ?? 0,
                                inputAttributes: { min: 0, step: 1 },
                                showCancelButton: true,
                                confirmButtonText: t("common.save") || "Enregistrer",
                                cancelButtonText: t("common.cancel") || "Annuler",
                                confirmButtonColor: "#198754",
                                cancelButtonColor: "#6c757d",
                                inputValidator: (value) => {
                                  if (value === null || value === "" || isNaN(value)) {
                                    return t("common.invalidValue") || "Veuillez saisir une valeur valide";
                                  }
                                  if (Number(value) < 0) {
                                    return t("common.mustBePositive") || "La quantité doit être positive";
                                  }
                                  return null;
                                },
                              }).then((res) => {
                                if (res.isConfirmed) {
                                  const newQte = Number(res.value);
                                  handleUpdateQte(stock.articleId, stock.entrepotId ?? selectedEntrepot?.value, newQte);
                                }
                              });
                            }}
                          >
                            <FaEdit />
                          </Button>
                        </td>

                        <td>{stock.stockMinimum}</td>

                        {/* Actions */}
                        <td className="d-flex gap-2">
                          <Button
                            size="sm"
                            color="warning"
                            outline
                            className="border shadow-sm"
                            title={t("stock.transfer") || "Transférer"}
                            onClick={() => openTransferModal(stock)}
                            disabled={!stock.articleId || !stock.entrepotId}
                          >
                            <FaExchangeAlt className="me-1" />
                            {t("stock.transfer") || "Transférer"}
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          </Row>

          {/* Transfer Modal */}
          <Modal isOpen={transferModal} toggle={toggleTransferModal} centered>
            <ModalHeader toggle={toggleTransferModal}>{t("stock.transferTitle") || "Transfert de stock"}</ModalHeader>

            <ModalBody>
              <div className="mb-3">
                <Label className="text-muted">{t("stock.article") || "Article"}</Label>
                <div className="fw-semibold">{transfer.articleNom || "-"}</div>
              </div>

              <Row>
                <Col md={12}>
                  <FormGroup>
                    <Label>{t("stock.entrepotSource") || "Entrepôt source"}</Label>
                    <Input value={transfer.entrepotSrcNom || ""} disabled />
                  </FormGroup>
                </Col>

                <Col md={12}>
                  <FormGroup>
                    <Label>{t("stock.entrepotDest") || "Entrepôt destination"}</Label>
                    <Select
                      options={entrepotOptions.filter((e) => e.value !== transfer.entrepotSrcId)}
                      value={entrepotOptions.find((o) => o.value === transfer.entrepotDestId) || null}
                      onChange={(opt) => setTransfer((s) => ({ ...s, entrepotDestId: opt?.value || null }))}
                      placeholder={t("stock.selectDest") || "Sélectionner destination"}
                      isSearchable
                    />
                  </FormGroup>
                </Col>

                <Col md={12}>
                  <FormGroup>
                    <Label>{t("stock.dateMouvement") || "Date du mouvement"}</Label>
                    <Input
                      type="date"
                      value={transfer.dateMouvement}
                      onChange={(e) => setTransfer((s) => ({ ...s, dateMouvement: e.target.value }))}
                    />
                  </FormGroup>
                </Col>

                <Col md={12}>
                  <FormGroup>
                    <Label>
                      {t("stock.transferQty") || "Quantité à transférer"}{" "}
                      <span className="text-muted">({t("stock.available") || "Dispo"}: {transfer.maxQuantite})</span>
                    </Label>
                    <Input
                      type="text"
                      inputMode="decimal"
                      value={transfer.quantite}
                      onWheel={(e) => e.currentTarget.blur()}
                      onKeyDown={(e) => {
                        if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                          e.preventDefault();
                        }
                      }}
                      onChange={(e) => {
                        const value = e.target.value.replace(",", ".");
                        if (/^\d*\.?\d*$/.test(value)) {
                          setTransfer((s) => ({ ...s, quantite: value }));
                        }
                      }}
                      placeholder="0"
                    />
                  </FormGroup>
                </Col>
              </Row>

              <div className="small text-muted">
                {t("stock.transferHint") ||
                  "Le transfert va décrémenter le stock de l’entrepôt source et incrémenter celui de l’entrepôt destination, avec création d’un mouvement TRANSFERT."}
              </div>
            </ModalBody>

            <ModalFooter>
              <Button color="secondary" onClick={toggleTransferModal}>
                {t("common.cancel") || "Annuler"}
              </Button>
              <Button color="primary" onClick={submitTransfer} disabled={stockLoading}>
                {t("common.save") || "Valider"}
              </Button>
            </ModalFooter>
          </Modal>
        </Container>
      )}
    </>
  );
};

export default StockPage;
